/**
 * Canonical icon registry — maps every icon name to its SVG component.
 *
 * Both the app (Metro) and Storybook (Vite) consume this single registry.
 * Each bundler resolves the bare `.svg` imports via its own transformer
 * (react-native-svg-transformer for Metro, vite-plugin-svgr for Vite).
 */
/* eslint-disable @typescript-eslint/no-unsafe-assignment -- SVG ambient module types resolve correctly for tsc but not for ESLint's type-aware parser */
import type { IconRegistry } from "./IconRegistry";

import Activity from "../../assets/icons/Activity.svg";
import Ai from "../../assets/icons/Ai.svg";
import AiOutline from "../../assets/icons/Ai-outline.svg";
import Alarm from "../../assets/icons/Alarm.svg";
import AlarmPause from "../../assets/icons/Alarm-pause.svg";
import ArrowDown from "../../assets/icons/Arrow-down.svg";
import ArrowLeft from "../../assets/icons/Arrow-left.svg";
import ArrowRight from "../../assets/icons/Arrow-right.svg";
import ArrowUp from "../../assets/icons/Arrow-up.svg";
import Bell from "../../assets/icons/Bell.svg";
import BellOffOutline from "../../assets/icons/Bell-off-outline.svg";
import BellOutline from "../../assets/icons/Bell-outline.svg";
import Bug from "../../assets/icons/Bug.svg";
import Building from "../../assets/icons/Building.svg";
import ReportDocument from "../../assets/icons/Report-document.svg";
import Calendar from "../../assets/icons/Calendar.svg";
import CalendarAvailable from "../../assets/icons/Calendar-available.svg";
import CalendarOutline from "../../assets/icons/Calendar-outline.svg";
import CalendarRefresh from "../../assets/icons/Calendar-refresh.svg";
import CalendarEvent from "../../assets/icons/Calendar-event.svg";
import Call from "../../assets/icons/Call.svg";
import Capture from "../../assets/icons/Capture.svg";
import Checkmark from "../../assets/icons/Checkmark.svg";
import CheckmarkOutline from "../../assets/icons/Checkmark-outline.svg";
import ChevronDown from "../../assets/icons/Chevron-down.svg";
import ChevronLeft from "../../assets/icons/Chevron-left.svg";
import ChevronRight from "../../assets/icons/Chevron-right.svg";
import ChevronUp from "../../assets/icons/Chevron-up.svg";
import Clock from "../../assets/icons/Clock.svg";
import ClockOutline from "../../assets/icons/Clock-outline.svg";
import Close from "../../assets/icons/Close.svg";
import CloseCircle from "../../assets/icons/Close-circle.svg";
import CloseCircleFilled from "../../assets/icons/Close-circle-filled.svg";
import CloudOff from "../../assets/icons/Cloud-off.svg";
import Crown from "../../assets/icons/Crown.svg";
import Cycle from "../../assets/icons/Cycle.svg";
import DeleteIcon from "../../assets/icons/Delete.svg";
import Domain from "../../assets/icons/Domain.svg";
import Dot from "../../assets/icons/Dot.svg";
import Download from "../../assets/icons/Download.svg";
import Edit from "../../assets/icons/Edit.svg";
import Emergency from "../../assets/icons/Emergency.svg";
import EmergencyOutline from "../../assets/icons/Emergency-outline.svg";
import ErrorIcon from "../../assets/icons/Error.svg";
import ErrorOutline from "../../assets/icons/Error-outline.svg";
import Event from "../../assets/icons/Event.svg";
import EventOutline from "../../assets/icons/Event-outline.svg";
import FolderOpen from "../../assets/icons/Folder-open.svg";
import Fullscreen from "../../assets/icons/Fullscreen.svg";
import FullscreenExit from "../../assets/icons/Fullscreen-exit.svg";
import HamburgerMenu from "../../assets/icons/Hamburger-menu.svg";
import Headphones from "../../assets/icons/Headphones.svg";
import Help from "../../assets/icons/Help.svg";
import Home from "../../assets/icons/Home.svg";
import HomeFilled from "../../assets/icons/Home-filled.svg";
import Hourglass from "../../assets/icons/Hourglass.svg";
import Infinite from "../../assets/icons/Infinite.svg";
import Info from "../../assets/icons/Info.svg";
import InfoOutline from "../../assets/icons/Info-outline.svg";
import KebabMenu from "../../assets/icons/Kebab-menu.svg";
import List from "../../assets/icons/List.svg";
import LockClosed from "../../assets/icons/Lock-closed.svg";
import LockOpen from "../../assets/icons/Lock-open.svg";
import Logout from "../../assets/icons/Logout.svg";
import Mail from "../../assets/icons/Mail.svg";
import MapIcon from "../../assets/icons/Map.svg";
import Meeting from "../../assets/icons/Meeting.svg";
import MessageBubble from "../../assets/icons/Message-bubble.svg";
import MessageBubbleOutline from "../../assets/icons/Message-bubble-outline.svg";
import MessagePending from "../../assets/icons/Message-pending.svg";
import Microphone from "../../assets/icons/Microphone.svg";
import Minus from "../../assets/icons/Minus.svg";
import Motion from "../../assets/icons/Motion.svg";
import Move from "../../assets/icons/Move.svg";
import Open from "../../assets/icons/Open.svg";
import OpenInNew from "../../assets/icons/Open-in-new.svg";
import Pause from "../../assets/icons/Pause.svg";
import Pending from "../../assets/icons/Pending.svg";
import PersonMoving from "../../assets/icons/Person-moving.svg";
import PersonShield from "../../assets/icons/Person-shield.svg";
import PictureOutline from "../../assets/icons/Picture-outline.svg";
import Play from "../../assets/icons/Play.svg";
import PlayArrow from "../../assets/icons/Play-arrow.svg";
import Plus from "../../assets/icons/Plus.svg";
import Pricetag from "../../assets/icons/Pricetag.svg";
import Recording from "../../assets/icons/Recording.svg";
import Refresh from "../../assets/icons/Refresh.svg";
import Resume from "../../assets/icons/Resume.svg";
import Rotate from "../../assets/icons/Rotate.svg";
import Search from "../../assets/icons/Search.svg";
import Send from "../../assets/icons/Send.svg";
import SendOutline from "../../assets/icons/Send-outline.svg";
import SendRotated from "../../assets/icons/Send-rotated.svg";
import Settings from "../../assets/icons/Settings.svg";
import SettingsCamera from "../../assets/icons/Settings-camera.svg";
import SettingsCameraOutline from "../../assets/icons/Settings-camera-outline.svg";
import SettingsOutline from "../../assets/icons/Settings-outline.svg";
import Share from "../../assets/icons/Share.svg";
import Silence from "../../assets/icons/Silence.svg";
import Siren from "../../assets/icons/Siren.svg";
import SirenDown from "../../assets/icons/Siren-down.svg";
import SkipNext from "../../assets/icons/Skip-next.svg";
import SkipPrevious from "../../assets/icons/Skip-previous.svg";
import Sort from "../../assets/icons/Sort.svg";
import Speaker from "../../assets/icons/Speaker.svg";
import Star from "../../assets/icons/Star.svg";
import SwapVertical from "../../assets/icons/Swap-vertical.svg";
import Switches from "../../assets/icons/Switches.svg";
import Switch from "../../assets/icons/Switch.svg";
import Tick from "../../assets/icons/Tick.svg";
import Toolbox from "../../assets/icons/Toolbox.svg";
import ToolboxOutline from "../../assets/icons/Toolbox-outline.svg";
import Truck from "../../assets/icons/Truck.svg";
import Tune from "../../assets/icons/Tune.svg";
import Undo from "../../assets/icons/Undo.svg";
import User from "../../assets/icons/User.svg";
import UserCircle from "../../assets/icons/User-circle.svg";
import UserCircleOutline from "../../assets/icons/User-circle-outline.svg";
import UserOutline from "../../assets/icons/User-outline.svg";
import Users from "../../assets/icons/Users.svg";
import Video from "../../assets/icons/Video.svg";
import VideoOff from "../../assets/icons/Video-off.svg";
import VideoOutline from "../../assets/icons/Video-outline.svg";
import VideoRecording from "../../assets/icons/Video-recording.svg";
import Visibility from "../../assets/icons/Visibility.svg";
import VisibilityOff from "../../assets/icons/Visibility-off.svg";
import VolumeOn from "../../assets/icons/Volume-on.svg";
import VolumeOff from "../../assets/icons/Volume-off.svg";
import Wand from "../../assets/icons/Wand.svg";
import Warning from "../../assets/icons/Warning.svg";
import WarningOutline from "../../assets/icons/Warning-outline.svg";
import WatchLive from "../../assets/icons/Watch-live.svg";

export const iconRegistry: IconRegistry = {
  activity: Activity,
  ai: Ai,
  aiOutline: AiOutline,
  alarm: Alarm,
  alarmPause: AlarmPause,
  arrowDown: ArrowDown,
  arrowLeft: ArrowLeft,
  arrowRight: ArrowRight,
  arrowUp: ArrowUp,
  bell: Bell,
  bellOffOutline: BellOffOutline,
  bellOutline: BellOutline,
  bug: Bug,
  building: Building,
  reportDocument: ReportDocument,
  calendar: Calendar,
  calendarAvailable: CalendarAvailable,
  calendarOutline: CalendarOutline,
  calendarRefresh: CalendarRefresh,
  calendarEvent: CalendarEvent,
  call: Call,
  capture: Capture,
  checkmark: Checkmark,
  checkmarkOutline: CheckmarkOutline,
  chevronDown: ChevronDown,
  chevronLeft: ChevronLeft,
  chevronRight: ChevronRight,
  chevronUp: ChevronUp,
  clock: Clock,
  clockOutline: ClockOutline,
  close: Close,
  closeCircle: CloseCircle,
  closeCircleFilled: CloseCircleFilled,
  cloudOff: CloudOff,
  crown: Crown,
  cycle: Cycle,
  delete: DeleteIcon,
  domain: Domain,
  dot: Dot,
  download: Download,
  edit: Edit,
  emergency: Emergency,
  emergencyOutline: EmergencyOutline,
  error: ErrorIcon,
  errorOutline: ErrorOutline,
  event: Event,
  eventOutline: EventOutline,
  folderOpen: FolderOpen,
  fullscreen: Fullscreen,
  fullscreenExit: FullscreenExit,
  hamburgerMenu: HamburgerMenu,
  headphones: Headphones,
  help: Help,
  home: Home,
  homeFilled: HomeFilled,
  hourglass: Hourglass,
  infinite: Infinite,
  info: Info,
  infoOutline: InfoOutline,
  kebabMenu: KebabMenu,
  list: List,
  lockClosed: LockClosed,
  lockOpen: LockOpen,
  logout: Logout,
  mail: Mail,
  map: MapIcon,
  meeting: Meeting,
  messageBubble: MessageBubble,
  messageBubbleOutline: MessageBubbleOutline,
  messagePending: MessagePending,
  microphone: Microphone,
  minus: Minus,
  motion: Motion,
  move: Move,
  open: Open,
  openInNew: OpenInNew,
  pause: Pause,
  pending: Pending,
  personMoving: PersonMoving,
  personShield: PersonShield,
  pictureOutline: PictureOutline,
  play: Play,
  playArrow: PlayArrow,
  plus: Plus,
  pricetag: Pricetag,
  recording: Recording,
  refresh: Refresh,
  resume: Resume,
  rotate: Rotate,
  search: Search,
  send: Send,
  sendOutline: SendOutline,
  sendRotated: SendRotated,
  settings: Settings,
  settingsCamera: SettingsCamera,
  settingsCameraOutline: SettingsCameraOutline,
  settingsOutline: SettingsOutline,
  share: Share,
  silence: Silence,
  siren: Siren,
  sirenDown: SirenDown,
  skipNext: SkipNext,
  skipPrevious: SkipPrevious,
  sort: Sort,
  speaker: Speaker,
  star: Star,
  swapVertical: SwapVertical,
  switches: Switches,
  switch: Switch,
  tick: Tick,
  toolbox: Toolbox,
  toolboxOutline: ToolboxOutline,
  truck: Truck,
  tune: Tune,
  undo: Undo,
  user: User,
  userCircle: UserCircle,
  userCircleOutline: UserCircleOutline,
  userOutline: UserOutline,
  users: Users,
  video: Video,
  videoOff: VideoOff,
  videoOutline: VideoOutline,
  videoRecording: VideoRecording,
  visibility: Visibility,
  visibilityOff: VisibilityOff,
  volumeOn: VolumeOn,
  volumeOff: VolumeOff,
  wand: Wand,
  warning: Warning,
  warningOutline: WarningOutline,
  watchLive: WatchLive,
};
